import React, { useEffect, useState } from 'react';
import { AdminLayout } from '../layout/AdminLayout';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { QrCode, Search, Trash2, ExternalLink, BarChart3 } from 'lucide-react';
import { toast } from 'sonner';

const API = (typeof window !== "undefined" && window.location ? window.location.origin : process.env.REACT_APP_BACKEND_URL || "");

export function QrScansPage() {
    const [scans, setScans] = useState([]);
    const [stats, setStats] = useState(null);
    const [search, setSearch] = useState('');
    const [loading, setLoading] = useState(true);

    const token = localStorage.getItem('bgpr_token');
    const headers = { Authorization: `Bearer ${token}` };

    const loadData = async () => {
        try {
            const [scansRes, statsRes] = await Promise.all([
                fetch(`${API}/api/qr-scans`, { headers }),
                fetch(`${API}/api/qr-scans/stats`, { headers }),
            ]);
            if (scansRes.ok) setScans(await scansRes.json());
            if (statsRes.ok) setStats(await statsRes.json());
        } catch (e) {
            toast.error('Fehler beim Laden der QR-Scans');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { loadData(); }, []);

    const handleDelete = async (id) => {
        if (!window.confirm('Diesen Scan-Eintrag löschen?')) return;
        const res = await fetch(`${API}/api/qr-scans/${id}`, { method: 'DELETE', headers });
        if (res.ok) {
            setScans(s => s.filter(x => x.id !== id));
            toast.success('Eintrag gelöscht');
        }
    };

    const filtered = scans.filter(s =>
        !search || s.instructor_name?.toLowerCase().includes(search.toLowerCase()) ||
        s.license_number?.toLowerCase().includes(search.toLowerCase())
    );

    const fmtDate = (iso) => {
        if (!iso) return '-';
        const d = new Date(iso);
        return d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
    };

    return (
        <AdminLayout>
            <div className="space-y-6" data-testid="qr-scans-page">
                <div className="flex items-center justify-between">
                    <div>
                        <h1 className="text-2xl font-bold">QR-Code Abrufe</h1>
                        <p className="text-sm text-muted-foreground mt-1">Alle Verifizierungsabrufe über QR-Code</p>
                    </div>
                </div>

                {stats && (
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                        <div className="bg-card border rounded-lg p-4" data-testid="stat-total">
                            <div className="flex items-center gap-3">
                                <div className="p-2 bg-blue-50 rounded-lg"><QrCode className="h-5 w-5 text-blue-600" /></div>
                                <div>
                                    <p className="text-2xl font-bold">{stats.total_scans}</p>
                                    <p className="text-xs text-muted-foreground">Abrufe gesamt</p>
                                </div>
                            </div>
                        </div>
                        <div className="bg-card border rounded-lg p-4" data-testid="stat-recent">
                            <div className="flex items-center gap-3">
                                <div className="p-2 bg-green-50 rounded-lg"><BarChart3 className="h-5 w-5 text-green-600" /></div>
                                <div>
                                    <p className="text-2xl font-bold">{stats.recent_30_days}</p>
                                    <p className="text-xs text-muted-foreground">Letzte 30 Tage</p>
                                </div>
                            </div>
                        </div>
                        <div className="bg-card border rounded-lg p-4" data-testid="stat-unique">
                            <div className="flex items-center gap-3">
                                <div className="p-2 bg-purple-50 rounded-lg"><ExternalLink className="h-5 w-5 text-purple-600" /></div>
                                <div>
                                    <p className="text-2xl font-bold">{stats.unique_licenses}</p>
                                    <p className="text-xs text-muted-foreground">Verschiedene Lizenzen</p>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                <div className="flex items-center gap-2">
                    <div className="relative flex-1 max-w-sm">
                        <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                        <Input
                            placeholder="Name oder Lizenznummer suchen..."
                            value={search}
                            onChange={e => setSearch(e.target.value)}
                            className="pl-8"
                            data-testid="qr-scan-search"
                        />
                    </div>
                    <span className="text-sm text-muted-foreground">{filtered.length} Einträge</span>
                </div>

                <div className="border rounded-lg overflow-hidden">
                    <table className="w-full text-sm">
                        <thead className="bg-muted/50">
                            <tr>
                                <th className="text-left p-3 font-medium">Zeitpunkt</th>
                                <th className="text-left p-3 font-medium">Name</th>
                                <th className="text-left p-3 font-medium">Lizenznummer</th>
                                <th className="text-left p-3 font-medium hidden md:table-cell">IP-Adresse</th>
                                <th className="text-right p-3 font-medium w-16"></th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                <tr><td colSpan={5} className="p-8 text-center text-muted-foreground">Lade...</td></tr>
                            ) : filtered.length === 0 ? (
                                <tr><td colSpan={5} className="p-8 text-center text-muted-foreground">Keine QR-Code Abrufe vorhanden</td></tr>
                            ) : filtered.map(s => (
                                <tr key={s.id} className="border-t hover:bg-muted/30" data-testid={`qr-scan-row-${s.id}`}>
                                    <td className="p-3 whitespace-nowrap">{fmtDate(s.scanned_at)}</td>
                                    <td className="p-3 font-medium">{s.instructor_name || '-'}</td>
                                    <td className="p-3 font-mono text-xs">{s.license_number || '-'}</td>
                                    <td className="p-3 hidden md:table-cell text-muted-foreground text-xs">{s.ip_address || '-'}</td>
                                    <td className="p-3 text-right">
                                        <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-red-500" onClick={() => handleDelete(s.id)} data-testid={`delete-scan-${s.id}`}>
                                            <Trash2 className="h-3.5 w-3.5" />
                                        </Button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </AdminLayout>
    );
}
